import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

// Components & Icons
import { ReactComponent as BarOrange } from "../../../assets/images/Bar_Orange.svg";
import { ReactComponent as BarGreen } from "../../../assets/images/Bar_Green.svg";
import { ReactComponent as BarBlue } from "../../../assets/images/Bar_Blue.svg";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import PersonAddIcon from "@mui/icons-material/PersonAdd";

// Modals & Commons
import GnaAdmin from "../../Modals/Admin/GnaAdmin/GnaAdmin";
import ChargesContainer from "../../Modals/Admin/Charges/ChargesContainer";
import CompanyUserSelection from "../../Modals/Admin/CompanyUserSelection/CompanyUserSelection";
import OnboardingPopup from "./OnboardingPopup/OnboardingPopup";
import MakerChecker from "../MakerChecker/MakerChecker";
import TextInput from "../../../commons/Inputs/TextInput/TextInput";
import {
  ErrorMessage,
  SuccessMessage,
} from "../../../commons/Toast/ErrorMessage";

// Services & Redux
import {
  fetchAdmins,
  fetchCompany,
  fetchMetrices,
} from "../../../Redux/Slices/gnaAdminSlice";
import { adminCompanyAccess } from "../../../Services/GNA/AlterApi";
import { generateNoca } from "../../../Services/Schedule/schedule";
import Styles from "./SuperAdminMainDashboard.module.css";
import ConfigurePlatform from "../../Modals/Admin/ConfigurePlatform/ConfigurePlatform";

// --- Internal Helper Components ---

const MetricCard = ({ Icon, label, value }) => (
  <div className={Styles.metricItem}>
    <Icon />
    <div className={Styles.escrowContainer}>
      <div className={Styles.escrowHeading}>{label}</div>
      <div>{value || 0}</div>
    </div>
  </div>
);

const StatusBadge = ({ status }) => {
  const statusMap = {
    VERIFIED: { class: "accepted", label: "Verified" },
    UN_VERIFIED: { class: "pending", label: "Un-Verified" },
    REJECTED: { class: "rejected", label: "Rejected" },
    HOLD: { class: "rejected", label: "Hold" },
  };
  const config = statusMap[status] || { class: "rejected", label: "InActive" };
  return <span className={config.class}>{config.label}</span>;
};

// --- Main Component ---

const SuperAdminMainDashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Redux Selectors
  const metrics = useSelector(
    (state) => state?.gnaAdminSlice?.fetchMetrices?.data,
  );
  const companies = useSelector(
    (state) => state?.gnaAdminSlice?.fetchCompany?.data,
  );
  const admins = useSelector(
    (state) => state?.gnaAdminSlice?.fetchAdmins?.data,
  );
  const role = useSelector((state) => state?.persistedReducer?.userType);

  // UI States grouped into one object
  const [ui, setUi] = useState({
    adminModal: false,
    chargesModal: false,
    selectionModal: false,
    onboardModal: false,
    searchTab: false,
    viewType:
      role === "MAKER" || role === "CHECKER" ? "MakerChecker" : "Company",
  });

  const [search, setSearch] = useState("");
  const [selectedAdmins, setSelectedAdmins] = useState([]);
  const [activeCompany, setActiveCompany] = useState({});
  const [viewUI, setViewUI] = useState(
    role === "MAKER" || role === "CHECKER" ? "MakerChecker" : "Company",
  );

  const [configureModal, setConfigureModal] = useState(false);

  useEffect(() => {
    dispatch(fetchMetrices());
    dispatch(fetchCompany());
    dispatch(fetchAdmins());
  }, [dispatch]);

  // Derived State: Filtered Companies (Optimized with useMemo)
  const filteredCompanies = useMemo(() => {
    if (!search) return companies || [];
    return companies.filter((c) =>
      c.companyName.toLowerCase().includes(search.toLowerCase()),
    );
  }, [search, companies]);

  // Logic Handlers
  const handleAdminAssignment = async (data) => {
    const payload = data.map((userId) => ({
      companyId: activeCompany?.companyId,
      adminUserId: userId,
      adminRole: admins?.find((a) => a.userId === userId)?.userType,
    }));

    const res = await adminCompanyAccess(payload);
    if (res?.data?.code === 200) {
      dispatch(fetchCompany());
      setUi((prev) => ({ ...prev, selectionModal: false }));
    } else {
      ErrorMessage(res?.data?.error || "Failed to assign admins");
    }
  };

  const handleNocaDownload = async () => {
    const res = await generateNoca();
    if (res?.data?.code === 200) {
      SuccessMessage(res?.data?.message);
      const blob = new Blob([window.atob(res.data.data)], {
        type: "text/plain",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "transaction_data.txt";
      link.click();
    } else {
      ErrorMessage(res?.data?.message || "NOCA Generation Failed");
    }
  };

  const getRoleLabel = (roles = []) => {
    if (roles.includes("TRADER")) return "TRADER";
    if (roles.includes("BANKING")) return "DISCOM";
    if (roles.includes("GENERATOR") && roles.includes("CONSUMER"))
      return "PROSUMER";
    if (roles.includes("CONSUMER")) return "CONSUMER";
    if (roles.includes("GENERATOR")) return "GENERATOR";
    return "N/A";
  };

  const CompanyMainColumn = () => {
    return (
      <div className={Styles.detailsCompanyContainer}>
        <>
          <div className="display_flex_row tableHeading">
            <div style={{ width: "25%" }} className={Styles.companyNameCont}>
              {ui.searchTab ? (
                <TextInput
                  value={search}
                  onChange={setSearch}
                  example="Search Company Name"
                  height="35"
                />
              ) : (
                <span>Company Name</span>
              )}
              {ui.searchTab ? (
                <ClearIcon
                  onClick={() => {
                    setSearch("");
                    setUi({ ...ui, searchTab: false });
                  }}
                  style={{ cursor: "pointer", fontSize: "16px" }}
                />
              ) : (
                <SearchIcon
                  onClick={() => setUi({ ...ui, searchTab: true })}
                  style={{ cursor: "pointer", fontSize: "16px" }}
                />
              )}
            </div>
            <div style={{ width: "10%" }}>Role</div>
            <div style={{ width: "15%" }}>Date of Registration</div>
            <div style={{ width: "10%" }}>Status</div>
            <div style={{ width: "20%" }}>VID Balance(₹)</div>
            {/* {role === "SUPER_ADMIN" && (
                <div style={{ width: "15%" }}>Assigned Admin</div>
              )} */}
            <div style={{ width: "5%" }}>View</div>
          </div>

          <div className={`scroll_bar_y ${Styles.companymainDetails}`}>
            {filteredCompanies.map((company) => {
              const totalEscrow = [
                ...(company?.consumers || []),
                ...(company?.generators || []),
              ].reduce(
                (sum, item) =>
                  sum +
                  (item?.walletAccount?.amountRemaining || 0) +
                  (item?.walletAccount?.amountBlocked || 0),
                0,
              );

              return (
                <div
                  className="display_flex_row tableDetails"
                  key={company.companyId}
                >
                  <div style={{ width: "25%" }}>{company?.companyName}</div>
                  <div style={{ width: "10%" }}>
                    {getRoleLabel(company?.roles)}
                  </div>
                  <div style={{ width: "15%" }}>{company?.registeredAt}</div>
                  <div style={{ width: "10%" }}>
                    <StatusBadge status={company?.accountStatus} />
                  </div>
                  <div style={{ width: "20%" }}>
                    <b>{company?.totalEscrowAmount?.toFixed(2)}</b>
                  </div>

                  {/* {role === "SUPER_ADMIN" && (
                      <div style={{ width: "15%" }}>
                        {company?.assignedAdmin?.length > 0 ? (
                          <div className="display_flex_row_gap_8">
                            <span>{company.assignedAdmin.length}</span>
                            <VisibilityIcon
                              className="pointer_icon"
                              style={{
                                color: "#f1935c",
                                fontSize: "16px",
                                cursor: "pointer",
                              }}
                              onClick={() => {
                                setActiveCompany(company);
                                setSelectedAdmins(
                                  company.assignedAdmin.map((a) => a.userId),
                                );
                                setUi({ ...ui, selectionModal: true });
                              }}
                            />
                          </div>
                        ) : (
                          <PersonAddIcon
                            className="pointer_icon"
                            style={{
                              color: "#f1935c",
                              fontSize: "16px",
                              cursor: "pointer",
                            }}
                            onClick={() => {
                              setActiveCompany(company);
                              setUi({ ...ui, selectionModal: true });
                            }}
                          />
                        )}
                      </div>
                    )} */}
                  <div style={{ width: "5%" }}>
                    <VisibilityIcon
                      style={{
                        color: "#f1935c",
                        fontSize: "16px",
                        cursor: "pointer",
                      }}
                      onClick={() =>
                        navigate(
                          `/admin_dashboard_company?id=${company.companyId}`,
                        )
                      }
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </>
      </div>
    );
  };

  return (
    <div className={Styles.mainContainer_sa}>
      {/* Modals */}
      {configureModal && (
        <ConfigurePlatform
          open={configureModal}
          handleClose={() => setConfigureModal(false)}
        />
      )}
      {ui.chargesModal && (
        <ChargesContainer
          open
          handleClose={() => setUi({ ...ui, chargesModal: false })}
        />
      )}
      {ui.selectionModal && (
        <CompanyUserSelection
          open
          selected={selectedAdmins}
          type="Admin"
          handleSubmit={handleAdminAssignment}
          handleClose={() => setUi({ ...ui, selectionModal: false })}
          handleSelection={(id) =>
            setSelectedAdmins((prev) =>
              prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
            )
          }
        />
      )}
      {ui.adminModal && (
        <GnaAdmin
          open
          handleClose={() => setUi({ ...ui, adminModal: false })}
        />
      )}

      {/* Top Metrics Row */}
      <div className={`display_flex_row ${Styles.countMainContainer}`}>
        <MetricCard Icon={BarGreen} label="Company" value={companies?.length} />
        <MetricCard
          Icon={BarGreen}
          label="Consumer"
          value={metrics?.totalConsumers}
        />
        <MetricCard
          Icon={BarBlue}
          label="Generator"
          value={metrics?.totalGenerators}
        />
        <MetricCard
          Icon={BarOrange}
          label="Discom"
          value={metrics?.totalDiscoms}
        />
        <MetricCard
          Icon={BarGreen}
          label="Traders"
          value={metrics?.totalTraders}
        />
      </div>

      {/* Configuration Bar */}
      <div className={`display_flex_row ${Styles.configureElement}`}>
        <div
          className={Styles.createAdmin}
          style={{
            cursor:
              role === "SUPER_ADMIN" || role === "CHECKER"
                ? "pointer"
                : "no-drop",
          }}
          onClick={() =>
            role === "SUPER_ADMIN"
              ? setUi({ ...ui, adminModal: true })
              : role === "CHECKER"
                ? handleNocaDownload()
                : null
          }
        >
          <BarOrange />
          <div>{role === "SUPER_ADMIN" ? "GNA Admins" : "Generate NOCA"}</div>
        </div>

        <div
          className={Styles.assignAdmin}
          onClick={() => setUi({ ...ui, chargesModal: true })}
        >
          <BarGreen />
          <div>Charges</div>
        </div>

        <div
          className={Styles.configurePlatform}
          onClick={() => setConfigureModal(true)}
        >
          <BarBlue />
          <div>Configure Platform</div>
        </div>

        <div className={Styles.createAdmin}>
          <BarOrange />
          <div className={Styles.escrowContainer}>
            <div className={Styles.escrowHeading}>Escrow Bal. (₹)</div>
            <div>
              {metrics?.totalEscrowAmountAvailable?.toFixed(2) || "0.00"}
            </div>
          </div>
        </div>
      </div>

      {/* Details Section */}
      {role === "MAKER" || role === "CHECKER" ? (
        <>
          <div className={Styles.filterSelectionContainer}>
            <div
              className={
                viewUI === "MakerChecker" &&
                Styles.filterSelectionActiveContainer
              }
              onClick={() => setViewUI("MakerChecker")}
            >
              Pending Work
            </div>
            <div
              className={
                viewUI === "MakerCheckerHistory" &&
                Styles.filterSelectionActiveContainer
              }
              onClick={() => setViewUI("MakerCheckerHistory")}
            >
              History
            </div>
            <div
              className={
                viewUI === "CompanyList" &&
                Styles.filterSelectionActiveContainer
              }
              onClick={() => setViewUI("CompanyList")}
            >
              Company List
            </div>
          </div>
          <div className={Styles.makerCheckerMainContainer}>
            {viewUI === "MakerChecker" && <MakerChecker type="PENDING" />}
            {viewUI === "MakerCheckerHistory" && (
              <MakerChecker type="HISTORY" />
            )}
            {viewUI === "CompanyList" && CompanyMainColumn()}
          </div>
        </>
      ) : (
        <div className={Styles.detailsMainCompanyContainer}>
          {CompanyMainColumn()}
        </div>
      )}
    </div>
  );
};

export default SuperAdminMainDashboard;
